<script lang="ts">
	import type { PoiCategory, PoiData } from '$lib/components/pois/types';
	import type { Page } from '@sveltejs/kit';
	import type { PageData } from './$types';
	import { page as pageStore } from '$app/stores';
	import NoRoomsImage from '$assets/availability/no-rooms.svg';
	import Logo from '$assets/logos/vm-logo-bib-michelin.svg';
	import AdStickyForPoiList from '$lib/components/advertising/AdStickyForPoiList.svelte';
	import Launchpad from '$lib/components/advertising/launchpad/Launchpad.svelte';
	import Footer from '$lib/components/footer/Footer.svelte';
	import { computeSearchParamsItinerary } from '$lib/components/itinerary/util';
	import { getMapLayoutContext } from '$lib/components/map/map-layout-context';
	import MapSearchPanel from '$lib/components/map/MapSearchPanel.svelte';
	import { getSelectedAddress } from '$lib/components/map/selectedAddress.svelte';
	import Pagination from '$lib/components/pagination/Pagination.svelte';
	import PoiList from '$lib/components/pois/list/PoiList.svelte';
	import {
		getPoiDetailLink,
		isDatasheetPoiData,
		isMainPoiCategory,
		mapPoiToCategory,
	} from '$lib/components/pois/utils';
	import ViewMore from '$lib/components/viewMore/ViewMore.svelte';
	import { HANDLE_BUTTON_HEIGHT } from '$lib/constants/drawer';
	import { getPageUrl } from '$lib/seo/utils';
	import { createBookingcomUrlWithLabel } from '$lib/services/bookingcom/urlWithLabel';
	import { track } from '$lib/services/tracking/googleAnalytics';
	import { getViewMoreOnTrackingEvent } from '$lib/services/tracking/googleAnalytics/pois';
	import { isHomePage } from '$lib/services/urls/routeIds';
	import {
		getSearchParamsStore,
		gotoWithParams,
		type SearchParamsType,
	} from '$lib/services/urls/searchParams';
	import { isVanitySearchParams } from '$lib/services/urls/vanity';
	import * as m from '$paraglide/messages';
	import { onMount, tick } from 'svelte';
	import { get } from 'svelte/store';

	let addSnap = false;

	export let data: PageData;
	$: ({ page, poisStore, seoDataStore, partnersLinks } = data);
	$: seo = $seoDataStore;

	const mapLayoutContext = getMapLayoutContext();
	const {
		isSummaryOpen,
		drawerScrollPosition,
		toggleDrawerScrollPosition,
		itineraryStore,
		moveDrawer,
		getPartialDrawerPosition,
		isPinMarkerOnMapWithSummary,
		mapPoiCategories,
		onAccommodationSelectDates,
		onAccommodationClearDates,
	} = mapLayoutContext;

	const searchParams = getSearchParamsStore();

	onMount(() => {
		addSnap = true;

		// Force the drawer to be at the center of the screen on first load
		// This is useful for Safari browser as it recomputes the scroll position after the first render
		// after the scroll snap points are added to the container
		setTimeout(() => moveDrawer('partial'), 300);
		$isPinMarkerOnMapWithSummary = Boolean($searchParams?.selectedPoi) ?? false;
	});

	let isSearchInputFocused = false;

	$: ({
		poisList,
		poisCount,
		pageCount,
		selectedAddress: initialSelectedAddress,
		selectedAddressCenter,
		poiCategories,
		availabilityStatus,
		mapBounds,
	} = $poisStore);
	$: hasOnlyAccommodation = poiCategories.length === 1 && poiCategories[0] === 'ACCOMMODATION';

	function trackPagination(page: number) {
		track({
			action: 'pagination',
			payload: {
				page_number: page,
			},
		});
	}

	$: address = initialSelectedAddress?.address || '';

	function rebuildSelectedAddress() {
		// TODO: find another way to requesting pois with coords instead of the address in case of a canonical page
		// NOTE: boundsSync = defined if use coords or address
		const boundsSync = $searchParams?.poiCategories?.length !== 2;
		const entityType = initialSelectedAddress?.entityType || getSelectedAddress()?.entityType || 'CITY';

		return { ...initialSelectedAddress, address, entityType, boundsSync };
	}

	function handleCategoriesChange(poiCategories: PoiCategory[]) {
		mapPoiCategories.emitUpdate(poiCategories.filter(isMainPoiCategory), {
			// NOTE: keep the address after a change of poiCategories
			selectedAddress: rebuildSelectedAddress(),
		});
	}

	function pageRelativeHref(page: number, $pageStore: Page) {
		const url = getPageUrl($pageStore.url, page);
		return url.pathname + url.search;
	}

	async function addToItinerary(poi: PoiData) {
		itineraryStore.addToItinerary({ coordinates: poi.coordinates, label: poi.name });
		const itinerary = await computeSearchParamsItinerary(get(itineraryStore.itinerary));

		const updatedSearchParams = {
			...$searchParams,
			poiCategories,
			bounds: mapBounds,
			itinerary,
		};

		gotoWithParams('/routes', updatedSearchParams);
	}

	function makeBookingUrlForPoilistFallback() {
		// TODO: does coordinates always present?
		if (!$poisStore?.selectedAddress?.coordinates) {
			return undefined;
		}

		return createBookingcomUrlWithLabel().poilist_fallback({
			arrival: $poisStore.selectedAddress.address,
			arrivalCoordinates: $poisStore.selectedAddress.coordinates,
			searchParams: $searchParams,
		});
	}

	let searchContainer: HTMLElement;
	// Offset to keep the logo visible when the drawer is at the very bottom
	const mininumLogoOffset = 8;

	let slideOffset = 0;

	async function computeSlideOffset(_drawerScrollPosition: number, _isSearchInputFocused: boolean) {
		await tick();
		if (
			_isSearchInputFocused
			|| !searchContainer
			|| _drawerScrollPosition >= getPartialDrawerPosition()
		) {
			slideOffset = 0;
		} else {
			slideOffset = getPartialDrawerPosition() - _drawerScrollPosition;
			const maxDrawerScrollPosition = searchContainer.clientHeight - mininumLogoOffset;
			if (slideOffset > maxDrawerScrollPosition) {
				slideOffset = maxDrawerScrollPosition;
			}
		}
	}

	$: computeSlideOffset($drawerScrollPosition, isSearchInputFocused);

	const logoHeight = 24;

	function getLinkDetail(poi: PoiData) {
		const datasheetRoute = `/map/${mapPoiToCategory(poi)}/poi/${poi.urlIdentifier}`;
		const selectedPoi = { id: poi.id, type: mapPoiToCategory(poi) };
		let sP = { ...$searchParams, selectedPoi, bounds: mapBounds, poiCategories, selectedAddress: rebuildSelectedAddress() } as SearchParamsType;
		let withSearchParams = true;

		if (isDatasheetPoiData(poi)) {
			// Show vanity params only if there is a single poi type
			// Otherwise, on the homepage, clicking on a poi would select the poi type of that selected poi
			const withVanityParams = (poiCategories.length === 1 && isVanitySearchParams($pageStore.url));
			sP = withVanityParams ? sP : { ...sP, selectedPoi: undefined };
			withSearchParams = !withVanityParams;
		}

		return getPoiDetailLink(poi, sP, datasheetRoute, withSearchParams);
	}

	function verifyIfLaunchpadShouldBeVisible() {
		const poiCategoriesSp = $searchParams?.poiCategories ?? [];
		return (
			poiCategoriesSp === null
			|| poiCategoriesSp === undefined
			|| (Array.isArray(poiCategoriesSp) && poiCategoriesSp.length === 0)
		);
	}

	$: shouldShowLaunchpad = isHomePage($pageStore.url) && verifyIfLaunchpadShouldBeVisible();

	$: isMultiPoi = poiCategories.length > 1;

	$: shouldShowPoilist = !shouldShowLaunchpad;
</script>

<div
	class="{isSearchInputFocused ? '' : 'translate-slide-offset'} {$isSummaryOpen
		? 'hidden md:block'
		: 'md:block'}"
	style={`--slide-offset: ${slideOffset}px;`}
>
	<img
		src={Logo}
		alt="Michelin"
		class="absolute top-[var(--top)] right-[var(--right)] md:hidden"
		style={`--top: ${-logoHeight}px; --right: ${mininumLogoOffset}px;`}
		height={logoHeight}
		width="120"
	/>
	<div bind:this={searchContainer}>
		<MapSearchPanel
			{initialSelectedAddress}
			{selectedAddressCenter}
			{mapBounds}
			{poiCategories}
			bind:isSearchInputFocused
			onSelectedAddressChange={() => {
				setTimeout(() => moveDrawer('partial'), 300);
			}}
		/>
	</div>
</div>

<div
	class="poi-container relative z-1 md:block bg-neutral-weakest md:bg-transparent"
	class:hidden={isSearchInputFocused || $isSummaryOpen}
	class:snap={addSnap}
>
	<button
		type="button"
		class="handle relative w-full p-4 rd-2 b-0 grid place-items-center bg-transparent md:hidden"
		class:snap={addSnap}
		on:click={() => toggleDrawerScrollPosition()}
		style:height="{HANDLE_BUTTON_HEIGHT}px"
	>
		<span class="rd-2 w-20 h-2 bg-base-15"></span>
	</button>
	<div class="mt-4">
		<PoiList
			{poisList}
			detailLink={getLinkDetail}
			selectedPoiCategories={$mapPoiCategories}
			showList={shouldShowPoilist}
			showMichelinSwitchFilter={shouldShowPoilist}
			withPartner={$pageStore.data.hasPartnerAdinmap}
			isLoading={$poisStore.fetching && poisList.length === 0}
			onPoiCategoriesChange={handleCategoriesChange}
			onAddItineraryClick={addToItinerary}
			onShowFilters={() => {
				gotoWithParams('/map/filters', {
					...$searchParams,
					poiCategories,
					bounds: mapBounds,
				});
			}}
			onCardClick={() => {
				$isPinMarkerOnMapWithSummary = true;
			}}
			onSelectDates={onAccommodationSelectDates}
			onClearDates={onAccommodationClearDates}
		/>
		{#if shouldShowLaunchpad}
			<Launchpad class="mt--2" />
		{/if}
	</div>

	{#if hasOnlyAccommodation && pageCount === page && availabilityStatus?.code && shouldShowPoilist}
		{@const bookingComUrl = makeBookingUrlForPoilistFallback()}
		{#if bookingComUrl}
			<div class="flex p-t-4 p-b-0 px-4 flex-col items-center gap-4 self-stretch">
				<ViewMore
					bookUrl={bookingComUrl}
					image={NoRoomsImage}
					textViewMoreOn={m.view_more__on()}
					textViewMoreOnPartnerWebsite={m.view_more__on_partner_website()}
					altText={m.view_more__no_room()}
					textViewMoreGoToPartnerWebsite={m.view_more__go_to_partner_website()}
					onHandleClick={() => {
						track(getViewMoreOnTrackingEvent());
					}}
				/>
			</div>
		{/if}
	{/if}

	{#if poisCount !== 0 && pageCount > 1 && shouldShowPoilist}
		<Pagination
			class="relative my-4"
			currentPage={page}
			totalPages={pageCount}
			onSelectPage={page => trackPagination(page)}
			pageHref={page => pageRelativeHref(page, $pageStore)}
			{isMultiPoi}
		/>
	{/if}

	{#if shouldShowPoilist}
		<AdStickyForPoiList />
	{/if}
	<Footer {seo} {partnersLinks} />
</div>

<style>
	.translate-slide-offset {
		transform: translate3d(0, var(--slide-offset), 0);
		transition: transform ease 0.05s;
	}

	@media screen and (min-width: 960px) {
		.translate-slide-offset {
			transform: none;
			transition: none;
		}
	}

	.handle.snap {
		scroll-snap-align: end;
	}

	/*
		* add a snap point at the top of the list with a margin of a third of screen height
		* so the list is visible on first load
	*/
	.poi-container {
		scroll-snap-align: start;
		scroll-margin: 60vh;
	}

	/*
		* we need a snap point at the very end of the list
		* otherwise the browser might go back to the top snap point on reaching it
	*/
	.poi-container.snap :global(:last-child) {
		scroll-snap-align: start;
		scroll-margin: 1rem;
	}
</style>
